import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { requestCache, CacheTTL } from '../utils/requestCache';

// API base configuration
// TEMPORARY: Hardcode production URL to test deployment
const API_BASE_URL = import.meta.env.PROD 
  ? 'https://web-production-d0043.up.railway.app/api'
  : (import.meta.env.VITE_API_URL || 'http://localhost:8001/api');

// Debug: Log the API URL being used (remove after debugging)
console.log('🔧 API_BASE_URL:', API_BASE_URL);
console.log('🔧 MODE:', import.meta.env.MODE);
console.log('🔧 PROD:', import.meta.env.PROD);

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for Django session authentication
});

// Function to get CSRF token
const getCSRFToken = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/csrf-token/`, {
      withCredentials: true,
    });
    return response.data.csrfToken;
  } catch (error) {
    console.warn('Could not get CSRF token:', error);
    return null;
  }
};

// Request interceptor to add CSRF token for POST/PUT/DELETE requests
api.interceptors.request.use(
  async (config) => {
    // Add CSRF token for POST/PUT/DELETE requests
    if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
      const csrfToken = await getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  timezone?: number;
  theme_preference?: 'light' | 'dark';
  is_staff: boolean;
  is_superuser: boolean;
}

export interface Company {
  id: number;
  name: string;
  company_type: 'consultant' | 'client';
  consultant_parent?: number;
  slug?: string;
  postcode?: string;
  city?: string;
  country?: number;
  employee_count?: number;
  sub_industry?: number; // Legacy field
  industry_level04?: number; // New field
  industry?: string; // Industry name from industry_level04 relationship
  site_count?: number; // Legacy static field
  sites_count?: number; // Dynamic count from CompanySites table
  website_url?: string | null; // Company website for AI-powered profile enrichment
  company_research?: CompanyEnrichmentResponse | null; // Stored AI research data
  company_research_date?: string | null; // When research was last fetched
  kpi_tracking_frequency?: 'weekly' | 'monthly' | 'quarterly'; // KPI tracking frequency
  kpi_display_periods?: number; // Number of periods to display on KPI dashboard
  created_at: string;
  updated_at: string;
  // Multi-tenant fields (populated by API)
  user_role?: string;
  is_primary_user?: boolean;
  can_manage_templates?: boolean;
  can_manage_clients?: boolean;
  client_companies?: Company[];
}

// Company enrichment response from LLM
export interface CompanyEnrichmentResponse {
  success: boolean;
  company_name: string;
  website_url?: string;
  full_profile: string;
  structured_data?: {
    company_description?: string;
    founded_year?: string | null;
    headquarters_city?: string | null;
    headquarters_country?: string | null;
    employee_count_estimate?: string | null;
    industry_keywords?: string[];
    is_manufacturer?: boolean | null;
    holds_inventory?: boolean | null;
    confidence_level?: 'high' | 'medium' | 'low';
  };
}

export interface UserPermissions {
  can_manage_templates: boolean;
  can_manage_clients: boolean;
  is_super_admin: boolean;
}

export interface CompanyListResponse {
  consultant_companies: Company[];
  client_companies: Company[];
  user_permissions: UserPermissions;
}

export interface CompanySite {
  id: number;
  company: number;
  name: string;
  engineers: boolean;
  manufacturers_internal: boolean;
  manufacturers_outsourced: boolean;
  purchases: boolean;
  holds_inventory: boolean;
  raw_material_part_count: number;
  finished_good_part_count: number;
}

export interface CompanyUser {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  first_name?: string;
  last_name?: string;
  is_primary_user: boolean;
  role?: string;
  is_active?: boolean;
  assigned_date?: string;
}

export interface Country {
  id: number;
  name: string;
  alpha2?: string;
  alpha3?: string;
  numeric?: number;
}

export interface Timezone {
  id: number;
  name: string;
  current_offset?: string;
  country: number;
  country_name?: string;
  country_code: string;
}

export interface GICS {
  id: number;
  sector_code: number;
  sector: string;
  industry_group_code: number;
  industry_group: string;
  industry_code: number;
  industry: string;
  sub_industry_code: number;
  sub_industry: string;
  detail: string;
}

export interface IndustryLevel01 {
  id: number;
  code: string;
  name: string;
}

export interface IndustryLevel02 {
  id: number;
  level01: number;
  code: string;
  name: string;
}

export interface IndustryLevel03 {
  id: number;
  level02: number;
  code: string;
  name: string;
}

export interface IndustryLevel04 {
  id: number;
  level03: number;
  code: string;
  name: string;
  detail?: string;
}

export interface IndustrySearchNode {
  id: number;
  code: string;
  name: string;
  detail?: string;
}

export interface IndustrySearchResult {
  level01: IndustrySearchNode;
  level02: IndustrySearchNode;
  level03: IndustrySearchNode;
  level04: IndustrySearchNode;
  display: string;
}

export interface AuditSeries {
  id: number;
  name: string;
  icon_name: string;
  
  // Multi-tenant template ownership
  created_by?: number;
  consultant_owner?: number;
  
  // Template sharing
  is_template?: boolean;
  is_shared_community?: boolean;
  community_rating?: number;
  usage_count?: number;
  
  // Client visibility control
  client_visibility?: 'all' | 'selected' | 'none';
  assigned_client_count?: number | null;
  
  // AI generation tracking
  is_ai_generated?: boolean;
  ai_generation_topic?: string;
  ai_generation_date?: string;
  
  // Template status and workflow
  status?: 'draft' | 'published';
  wizard_step?: number | null;
  
  // Template description and focus areas
  description?: string;
  key_focus_areas?: string;
  radar_category_level?: 1 | 2;
  
  // Question count
  question_count?: number;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

// Template client assignment types
export interface TemplateClientAssignment {
  id: number;
  name: string;
  assigned_at?: string;
  assigned_by?: string;
}

export interface TemplateClientAssignmentsResponse {
  template_id: number;
  template_name: string;
  client_visibility: 'all' | 'selected' | 'none';
  assigned_clients: TemplateClientAssignment[];
  available_clients: { id: number; name: string }[];
}

export interface UpdateClientVisibilityRequest {
  client_visibility: 'all' | 'selected' | 'none';
  assigned_client_ids: number[];
}

export interface UpdateClientVisibilityResponse {
  success: boolean;
  message: string;
  client_visibility: 'all' | 'selected' | 'none';
  assigned_clients: TemplateClientAssignment[];
  assigned_count: number;
}

export interface AuditQuestion {
  id: number;
  audit_series: number;
  audit_revision: number;
  question_seq: number;
  level01: string;
  level02: string;
  level03: string;
  level04: string;
  level05: string;
  description: string;
  mandatory_beneficial: string;
  question_type: string;
  short_text?: string;
  long_text?: string;
  options?: AuditQuestionOption[];
}

export interface AuditQuestionOption {
  id: number;
  question_header: number;
  audit_revision: number;
  option_text: string;
  option_seq: number;
  score: number;
  action_item?: string;
}

export interface AuditSession {
  id: number;
  company: number;
  site: number;
  audit_series: number;
  start_date: string;
  end_date?: string;
  created_by: number;
  answers?: AuditAnswer[];
  // Additional fields from serializer
  site_name?: string;
  company_name?: string;
  audit_series_name?: string;
}

export interface AuditAnswer {
  id: number;
  audit_header: number;
  company: number;
  site: number;
  user: number;
  question_header: number;
  question_detail: number;
  answered_at: string;
}

export interface ActionItem {
  id: number;
  title: string;
  description?: string;
  status: 'not_started' | 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  start_date?: string;
  due_date?: string;
  estimated_hours?: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  company_name: string;
  audit_name: string;
  site_name: string;
  audit_session_id: number;
  assigned_to?: number | null; // User ID
  assigned_to_name?: string; // Display name (computed from assigned_to)
  source_question_count: number;
  subtasks?: SubTask[];
  can_generate_subtasks?: boolean;
  comment_count?: number; // Number of comments on this action item
}

export interface SubTask {
  id: number;
  title: string;
  description?: string;
  status: 'not_started' | 'pending' | 'in_progress' | 'completed' | 'cancelled';
  assigned_to?: number | null; // User ID
  assigned_to_name?: string; // Display name (computed from assigned_to)
  start_date?: string;
  due_date?: string;
  comment_count?: number; // Number of comments on this subtask
  estimated_hours?: number;
  order: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface ActionItemsCompletionSummary {
  session_id: number;
  company_id: number;
  audit_name: string;
  site_name: string;
  total_action_items: number;
  completed_action_items: number;
  in_progress_action_items: number;
  total_subtasks: number;
  completed_subtasks: number;
  in_progress_subtasks: number;
  completion_percentage: number;
}

export interface AuditAnalysis {
  id: number;
  company: number;
  audit_header: number;
  analysis_text: string;
  analysis_date: string;
  overall_score: number;
  total_questions: number;
  completion_time: {
    total_minutes: number;
    total_hours: number;
    total_days: number;
    display: string;
    start_date: string;
    end_date: string;
  } | number | null;  // Support both old number format and new object format
  category_scores?: Array<{
    name: string;
    score: number;
    question_count: number;
    trend: string;
  }>;
  key_findings?: Array<{
    title: string;
    description: string;
    priority: string;
  }>;
  recommendations?: Array<{
    title: string;
    description: string;
    priority: string;
    actions?: string[];
  }>;
  detailed_results?: Array<{
    question_text: string;
    answer_text: string;
    answer_score: number;
    category: string;
  }>;
  historical_comparison?: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor: string;
      borderWidth: number;
      pointBackgroundColor: string;
      pointBorderColor: string;
      pointHoverBackgroundColor: string;
      pointHoverBorderColor: string;
    }>;
  };
}

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login/', { username, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout/');
    return response.data;
  },

  register: async (userData: {
    username: string;
    email: string;
    password: string;
    company_name: string;
    postcode?: string;
    city?: string;
    country_id?: number;
    employee_count?: number;
    sub_industry_id?: number;
    site_count?: number;
    engineers?: boolean;
    manufacturers_internal?: boolean;
    manufacturers_outsourced?: boolean;
    purchases?: boolean;
    holds_inventory?: boolean;
    raw_material_part_count?: number;
    finished_good_part_count?: number;
  }) => {
    const response = await api.post('/auth/register/', userData);
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/users/profile/');
    return response.data;
  },
};

// User API
export const userAPI = {
  getUsers: async () => {
    const response = await api.get('/users/');
    return response.data;
  },

  getUnassignedUsers: async () => {
    const response = await api.get('/users/?unassigned_only=true');
    return response.data;
  },

  getAssignableUsers: async (companyId: number) => {
    const response = await api.get(`/users/?assignable_for_company=${companyId}`);
    return response.data;
  },

  // Invite a new user - sends invitation email
  createUser: async (userData: {
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    const response = await api.post('/users/', userData);
    return response.data;
  },
};

// Company API
export const companyAPI = {
  getCompanies: async (): Promise<CompanyListResponse> => {
    // Use short cache (1 min) with deduplication to prevent multiple calls
    return requestCache.deduplicate(
      '/companies/',
      async () => {
        const response = await api.get('/companies/');
        return response.data;
      },
      CacheTTL.SHORT
    );
  },

  getClientCompanies: async (): Promise<Company[]> => {
    const data = await companyAPI.getCompanies();
    // Return both client companies and nested client companies from consultant companies
    const allClients = [...data.client_companies];
    data.consultant_companies.forEach(consultant => {
      if (consultant.client_companies) {
        allClients.push(...consultant.client_companies);
      }
    });
    return allClients;
  },

  getCompany: async (id: number) => {
    // Use medium cache (5 min) - company details don't change frequently
    return requestCache.deduplicate(
      `/companies/${id}/`,
      async () => {
        const response = await api.get(`/companies/${id}/`);
        return response.data;
      },
      CacheTTL.MEDIUM
    );
  },

  // OPTIMIZED: Get combined company dashboard data (company + analytics + branding)
  // No cache - analytics should be fresh on each page load
  getCompanyDashboard: async (id: number) => {
    // Use deduplication but no cache (analytics should be fresh)
    return requestCache.deduplicate(
      `/companies/${id}/dashboard/`,
      async () => {
        const response = await api.get(`/companies/${id}/dashboard/`);
        return response.data;
      },
      0 // No cache, but still deduplicate simultaneous requests
    );
  },

  createCompany: async (data: Partial<Company>) => {
    const response = await api.post('/companies/', data);
    // Invalidate company list cache
    requestCache.invalidatePattern('/companies');
    return response.data;
  },

  updateCompany: async (id: number, data: Partial<Company>) => {
    const response = await api.put(`/companies/${id}/`, data);
    // Invalidate company cache for this company and list
    requestCache.invalidate(`/companies/${id}/`);
    requestCache.invalidate(`/companies/${id}/dashboard/`);
    requestCache.invalidatePattern('/companies/');
    return response.data;
  },

  deleteCompany: async (id: number) => {
    const response = await api.delete(`/companies/${id}/`);
    // Invalidate company list cache
    requestCache.invalidatePattern('/companies');
    return response.data;
  },

  // Company Enrichment using LLM
  enrichCompany: async (companyName: string, websiteUrl?: string, companyId?: number): Promise<CompanyEnrichmentResponse> => {
    const response = await api.post('/companies/enrich/', {
      company_name: companyName,
      website_url: websiteUrl,
      company_id: companyId
    });
    return response.data;
  },

  // Company Users Management
  getCompanyUsers: async (companyId: number) => {
    const response = await api.get(`/companies/${companyId}/users/`);
    return response.data;
  },

  getAllUsers: async () => {
    const response = await api.get('/users/');
    return response.data;
  },

  assignUserToCompany: async (companyId: number, userId: number, isPrimary: boolean = false) => {
    const response = await api.post(`/companies/${companyId}/users/`, {
      user_id: userId,
      is_primary_user: isPrimary
    });
    return response.data;
  },

  removeUserFromCompany: async (companyId: number, userId: number) => {
    const response = await api.delete(`/companies/${companyId}/users/`, {
      data: { user_id: userId }
    });
    return response.data;
  },

  // Company Sites Management
  getCompanySites: async (companyId: number) => {
    const response = await api.get(`/companies/${companyId}/sites/`);
    return response.data;
  },

  getCompanySite: async (companyId: number, siteId: number) => {
    const response = await api.get(`/companies/${companyId}/sites/${siteId}/`);
    return response.data;
  },

  createCompanySite: async (companyId: number, data: Partial<CompanySite>) => {
    const response = await api.post(`/companies/${companyId}/sites/`, data);
    return response.data;
  },

  updateCompanySite: async (companyId: number, siteId: number, data: Partial<CompanySite>) => {
    const response = await api.put(`/companies/${companyId}/sites/${siteId}/`, data);
    return response.data;
  },

  deleteCompanySite: async (companyId: number, siteId: number) => {
    const response = await api.delete(`/companies/${companyId}/sites/${siteId}/`);
    return response.data;
  },
};

// Reference Data API
export const referenceAPI = {
  getCountries: async () => {
    // Cache for 1 hour - countries rarely change
    return requestCache.deduplicate(
      '/countries/',
      async () => {
        const response = await api.get('/countries/');
        return response.data;
      },
      CacheTTL.HOUR
    );
  },

  getTimezones: async () => {
    // Cache for 1 hour - timezones never change
    return requestCache.deduplicate(
      '/timezones/',
      async () => {
        const response = await api.get('/timezones/');
        return response.data;
      },
      CacheTTL.HOUR
    );
  },

  getGICS: async () => {
    // Cache for 1 hour - GICS codes rarely change
    return requestCache.deduplicate(
      '/gics/',
      async () => {
        const response = await api.get('/gics/');
        return response.data;
      },
      CacheTTL.HOUR
    );
  },
};

// Industry API
export const industryAPI = {
  getLevel01Industries: async (): Promise<IndustryLevel01[]> => {
    // Cache for 1 hour - industry classifications rarely change
    return requestCache.deduplicate(
      '/industries/level01/',
      async () => {
        const response = await api.get('/industries/level01/');
        return response.data;
      },
      CacheTTL.HOUR
    );
  },

  getLevel02Industries: async (level01Id?: number): Promise<IndustryLevel02[]> => {
    const params = level01Id ? { level01_id: level01Id } : {};
    // Cache for 1 hour with params as cache key
    return requestCache.deduplicate(
      '/industries/level02/',
      async () => {
        const response = await api.get('/industries/level02/', { params });
        return response.data;
      },
      CacheTTL.HOUR,
      params
    );
  },

  getLevel03Industries: async (level02Id?: number): Promise<IndustryLevel03[]> => {
    const params = level02Id ? { level02_id: level02Id } : {};
    // Cache for 1 hour with params as cache key
    return requestCache.deduplicate(
      '/industries/level03/',
      async () => {
        const response = await api.get('/industries/level03/', { params });
        return response.data;
      },
      CacheTTL.HOUR,
      params
    );
  },

  getLevel04Industries: async (level03Id?: number): Promise<IndustryLevel04[]> => {
    const params = level03Id ? { level03_id: level03Id } : {};
    // Cache for 1 hour with params as cache key
    return requestCache.deduplicate(
      '/industries/level04/',
      async () => {
        const response = await api.get('/industries/level04/', { params });
        return response.data;
      },
      CacheTTL.HOUR,
      params
    );
  },

  searchIndustries: async (query: string, limit: number = 50): Promise<IndustrySearchResult[]> => {
    const q = (query || '').trim();
    if (q.length < 2) {
      return [];
    }
    const params = { q, limit };
    // Cache for 1 hour with params as cache key - reference data rarely changes
    return requestCache.deduplicate(
      '/industries/search/',
      async () => {
        const response = await api.get('/industries/search/', { params });
        return response.data;
      },
      CacheTTL.HOUR,
      params
    );
  },
};

// Audit API
export const auditAPI = {
  // Get audit series - cached for 5 minutes with deduplication
  getAuditSeries: async (type: 'our' | 'community' = 'our', status: 'published' | 'draft' = 'published') => {
    return requestCache.deduplicate(
      `/audit/series/?type=${type}&status=${status}`,
      async () => {
        const response = await api.get('/audit/series/', { params: { type, status } });
        return response.data;
      },
      CacheTTL.MEDIUM // 5 minutes
    );
  },

  getAuditQuestions: async (auditSeriesId?: number) => {
    const params = auditSeriesId ? { audit_series_id: auditSeriesId } : {};
    const response = await api.get('/audit/questions/', { params });
    return response.data;
  },

  getAuditQuestion: async (id: number) => {
    const response = await api.get(`/audit/questions/${id}/`);
    return response.data;
  },

  // Get audit sessions - cached for 1 minute with deduplication
  getAuditSessions: async (companyId?: number) => {
    const cacheKey = companyId ? `/audit/sessions/?company_id=${companyId}` : '/audit/sessions/';
    return requestCache.deduplicate(
      cacheKey,
      async () => {
        const params = companyId ? { company_id: companyId } : {};
        const response = await api.get('/audit/sessions/', { params });
        return response.data;
      },
      CacheTTL.SHORT // 1 minute
    );
  },

  getAuditSession: async (id: number) => {
    const response = await api.get(`/audit/sessions/${id}/`);
    return response.data;
  },

  startAudit: async (data: {
    company_id: number;
    site_id: number;
    audit_series_id: number;
  }) => {
    const response = await api.post('/audit/sessions/start/', data);
    return response.data;
  },

  completeAudit: async (sessionId: number) => {
    const response = await api.post(`/audit/sessions/${sessionId}/complete/`);
    return response.data;
  },

  getAuditSessionDeleteInfo: async (sessionId: number) => {
    const response = await api.get(`/audit/sessions/${sessionId}/delete-info/`);
    return response.data;
  },

  deleteAuditSession: async (sessionId: number) => {
    const response = await api.delete(`/audit/sessions/${sessionId}/`);
    return response.data;
  },

  // Subtask generation background task APIs
  getSubtaskGenerationStatus: async (taskId: string) => {
    const response = await api.get(`/audit/subtask-generation/${taskId}/status/`);
    return response.data;
  },

  retrySubtaskGeneration: async (taskId: string) => {
    const response = await api.post(`/audit/subtask-generation/${taskId}/retry/`);
    return response.data;
  },

  getTemplateGenerationProgress: async (sessionId: string) => {
    const response = await api.get(`/audit/ai-builder/progress/${sessionId}/`);
    return response.data;
  },

  getActiveSubtaskTask: async (sessionId: number) => {
    const response = await api.get(`/audit/sessions/${sessionId}/active-task/`);
    return response.data;
  },

  submitAnswer: async (data: {
    session_id: number;
    question_header_id: number;
    question_detail_id: number;
  }) => {
    const response = await api.post('/audit/answers/submit/', data);
    return response.data;
  },

  saveProgress: async (data: {
    session_id: number;
    answers: Array<{
      question_header_id: number;
      question_detail_id: number;
    }>;
  }) => {
    const response = await api.post('/audit/answers/save-progress/', data);
    return response.data;
  },

  getAuditAnalysis: async (sessionId: number) => {
    const response = await api.get(`/audit/analysis/${sessionId}/`);
    return response.data;
  },

  // Get all analyses for a company in one request (bulk fetch)
  getBulkAuditAnalyses: async (companyId: number): Promise<{ analyses: Record<number, AuditAnalysis>; count: number }> => {
    const response = await api.get(`/audit/analysis/`, {
      params: { company_id: companyId }
    });
    return response.data;
  },

  generateAnalysis: async (sessionId: number) => {
    const response = await api.post(`/audit/analysis/${sessionId}/generate/`);
    return response.data;
  },

  getNextSteps: async (companyId?: number) => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await api.get('/audit/next-steps/', { params });
    return response.data;
  },

  getAuditReport: async (seriesId: number) => {
    const response = await api.get(`/audit/reports/${seriesId}/`);
    return response.data;
  },

  exportAuditPDF: async (sessionId: number) => {
    const response = await api.get(`/audit/sessions/${sessionId}/export-pdf/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  shareAuditResults: async (sessionId: number) => {
    const response = await api.post(`/audit/sessions/${sessionId}/share/`);
    return response.data;
  },

  cloneTemplate: async (templateId: number, data: { name: string }) => {
    const response = await api.post(`/audit/template/${templateId}/clone/`, data);
    return response.data;
  },

  updateTemplate: async (templateId: number, data: Partial<AuditSeries>) => {
    const response = await api.put(`/audit/template/${templateId}/update/`, data);
    return response.data;
  },
  rateTemplate: async (templateId: number, rating: number, comment?: string) => {
    const response = await api.post(`/audit/template/${templateId}/rate/`, { rating, comment });
    return response.data;
  },

  // Template Client Assignment APIs
  getTemplateClientAssignments: async (templateId: number): Promise<TemplateClientAssignmentsResponse> => {
    const response = await api.get(`/audit/template/${templateId}/clients/`);
    return response.data;
  },

  updateTemplateClientVisibility: async (
    templateId: number, 
    data: UpdateClientVisibilityRequest
  ): Promise<UpdateClientVisibilityResponse> => {
    const response = await api.put(`/audit/template/${templateId}/clients/update/`, data);
    return response.data;
  },
};

export const analyticsAPI = {
  // Get dashboard analytics - replace hardcoded portfolio metrics
  // Use deduplication but no cache (analytics should be relatively fresh)
  getDashboardAnalytics: async () => {
    return requestCache.deduplicate(
      '/analytics/dashboard/',
      async () => {
        const response = await api.get('/analytics/dashboard/');
        return response.data;
      },
      CacheTTL.SHORT // 1 minute cache - balance between freshness and performance
    );
  },

  // Get urgent items - replace hardcoded urgent items
  // Use deduplication but no cache (urgent items should be fresh)
  getUrgentItems: async () => {
    return requestCache.deduplicate(
      '/analytics/urgent-items/',
      async () => {
        const response = await api.get('/analytics/urgent-items/');
        return response.data;
      },
      CacheTTL.SHORT // 1 minute cache
    );
  },

  // Get client activity matrix (12 weeks x clients)
  getClientActivityMatrix: async () => {
    return requestCache.deduplicate(
      '/analytics/client-activity-matrix/',
      async () => {
        const response = await api.get('/analytics/client-activity-matrix/');
        return response.data;
      },
      CacheTTL.SHORT // 1 minute cache
    );
  },

  // Get company-specific analytics - replace hardcoded company metrics
  getCompanyAnalytics: async (companyId: number) => {
    return requestCache.deduplicate(
      `/companies/${companyId}/analytics/`,
      async () => {
        const response = await api.get(`/companies/${companyId}/analytics/`);
        return response.data;
      },
      0 // No cache, but deduplicate simultaneous requests
    );
  },

  // Get company status - replace hardcoded status logic
  getCompanyStatus: async (companyId: number) => {
    return requestCache.deduplicate(
      `/companies/${companyId}/status/`,
      async () => {
        const response = await api.get(`/companies/${companyId}/status/`);
        return response.data;
      },
      CacheTTL.MEDIUM // 5 minutes - status doesn't change frequently
    );
  },
};

export const teamAPI = {
  // Get all team members for the consultant company
  getTeamMembers: async () => {
    const response = await api.get('/team/');
    return response.data;
  },

  // Invite a new team member - sends invitation email
  addTeamMember: async (memberData: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
  }) => {
    const response = await api.post('/team/', memberData);
    return response.data;
  },

  // Update team member role
  updateTeamMember: async (memberId: number, memberData: { role: string }) => {
    const response = await api.put(`/team/${memberId}/`, memberData);
    return response.data;
  },

  // Remove team member (deactivate)
  removeTeamMember: async (memberId: number) => {
    const response = await api.delete(`/team/${memberId}/`);
    return response.data;
  },
};

// AI Template Builder API
export const aiBuilderAPI = {
  startTemplateGeneration: async (data: {
    topic: string;
    industry?: string;
    target_question_count: number;
    target_category_count?: number;
    additional_context?: string;
  }) => {
    try {
      const response = await api.post('/audit/ai-builder/start/', data);
      return response.data;
    } catch (error) {
      console.error('Error starting AI template generation:', error);
      throw error;
    }
  },

  saveTemplate: async (data: {
    session_id: string;
    existing_template_id?: number; // For updating existing drafts
    template_name: string;
    edited_structure?: any;
    client_visibility?: 'all' | 'selected' | 'none';
    kpis?: Array<{ name: string; description: string }>;
    status?: 'draft' | 'published';
    wizard_step?: number;
  }) => {
    try {
      const response = await api.post('/audit/ai-builder/save/', data);
      return response.data;
    } catch (error) {
      console.error('Error saving AI template:', error);
      throw error;
    }
  },

  getContextSuggestions: async (data: {
    topic: string;
    industry?: string;
    current_context?: string;
  }) => {
    try {
      const response = await api.post('/audit/ai-builder/context-suggestions/', data);
      return response.data;
    } catch (error) {
      console.error('Error getting context suggestions:', error);
      throw error;
    }
  },

  formatContext: async (data: {
    topic: string;
    industry?: string;
    original_context?: string;
    answers: Array<{ question: string; answer: string }>;
  }) => {
    try {
      const response = await api.post('/audit/ai-builder/format-context/', data);
      return response.data;
    } catch (error) {
      console.error('Error formatting context:', error);
      throw error;
    }
  },

  startTemplateAIAssist: async (data: {
    prompt: string;
    questions: any[];
    template_id?: number;
    topic?: string;
    industry?: string;
    additional_context?: string;
  }) => {
    const response = await api.post('/audit/ai-builder/assist/start/', data);
    return response.data;
  },

  getTemplateAIAssistProgress: async (sessionId: string) => {
    const response = await api.get(`/audit/ai-builder/assist/progress/${sessionId}/`);
    return response.data;
  },

  applyTemplateAIAssist: async (sessionId: string, selectedIndices?: number[]) => {
    const response = await api.post(`/audit/ai-builder/assist/apply/${sessionId}/`, {
      selected_indices: selectedIndices,
    });
    return response.data;
  },

  updateTemplate: async (templateId: number, templateData: Partial<AuditSeries>) => {
    try {
      const response = await api.put(`/audit/template/${templateId}/update/`, templateData);
      return response.data;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  },

  deleteTemplate: async (templateId: number) => {
    try {
      const response = await api.delete(`/audit/template/${templateId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  },

  publishTemplate: async (templateId: number) => {
    try {
      const response = await api.post(`/audit/series/${templateId}/publish/`);
      return response.data;
    } catch (error) {
      console.error('Error publishing template:', error);
      throw error;
    }
  },

  generateCommentary: async (companyId: number, forceRegenerate = false, siteId?: number) => {
    try {
      const response = await api.post('/audit/generate-commentary/', { 
        company_id: companyId,
        force_regenerate: forceRegenerate,
        site_id: siteId  // Optional: for site-specific commentary
      });
      return response.data;
    } catch (error) {
      console.error('Error generating AI commentary:', error);
      throw error;
    }
  },

  getActionItems: async (companyId: number, sessionId?: number) => {
    try {
      const params: { company_id: number; session_id?: number } = { company_id: companyId };
      if (sessionId) {
        params.session_id = sessionId;
      }
      const response = await api.get('/audit/action-items/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching action items:', error);
      throw error;
    }
  },

  getActionItemsCompletionSummary: async (sessionId: number) => {
    try {
      const response = await api.get(`/audit/action-items/completion/${sessionId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching completion summary:', error);
      throw error;
    }
  },

  deleteActionItemsByAudit: async (sessionId: number) => {
    try {
      const response = await api.delete(`/audit/action-items/delete-by-audit/${sessionId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting action items:', error);
      throw error;
    }
  },

  updateActionItem: async (actionItemId: number, data: Partial<ActionItem>) => {
    try {
      const response = await api.put(`/audit/action-items/${actionItemId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating action item:', error);
      throw error;
    }
  },

  // Sub-tasks
  getSubTasks: async (actionItemId: number) => {
    try {
      const response = await api.get(`/audit/action-items/${actionItemId}/subtasks/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sub-tasks:', error);
      throw error;
    }
  },

  createSubTask: async (actionItemId: number, data: Partial<SubTask>) => {
    try {
      const response = await api.post(`/audit/action-items/${actionItemId}/subtasks/`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating sub-task:', error);
      throw error;
    }
  },

  updateSubTask: async (subTaskId: number, data: Partial<SubTask>) => {
    try {
      const response = await api.put(`/audit/subtasks/${subTaskId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating sub-task:', error);
      throw error;
    }
  },

  deleteSubTask: async (subTaskId: number) => {
    try {
      const response = await api.delete(`/audit/subtasks/${subTaskId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting sub-task:', error);
      throw error;
    }
  },

  generateSubTasks: async (actionItemId: number) => {
    try {
      const response = await api.post(`/audit/action-items/${actionItemId}/generate-subtasks/`);
      return response.data;
    } catch (error) {
      console.error('Error generating sub-tasks:', error);
      throw error;
    }
  },

  getTemplate: async (templateId: number): Promise<AuditSeries> => {
    try {
      const response = await api.get(`/audit/template/${templateId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching template:', error);
      throw error;
    }
  },

  getTemplateQuestions: async (templateId: number) => {
    try {
      const response = await api.get(`/audit/template/${templateId}/questions/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching template questions:', error);
      throw error;
    }
  },

  updateTemplateQuestions: async (templateId: number, questions: any[]) => {
    try {
      const response = await api.put(`/audit/template/${templateId}/questions/update/`, { questions });
      return response.data;
    } catch (error) {
      console.error('Error updating template questions:', error);
      throw error;
    }
  },

  // Comments
  getComments: async (entityType: 'task' | 'subtask', entityId: number, offset: number = 0) => {
    try {
      const endpoint = entityType === 'task' 
        ? `/audit/action-items/${entityId}/comments/`
        : `/audit/subtasks/${entityId}/comments/`;
      
      const response = await api.get(endpoint, {
        params: { offset }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${entityType} comments:`, error);
      throw error;
    }
  },

  createComment: async (entityType: 'task' | 'subtask', entityId: number, commentText: string) => {
    try {
      const endpoint = entityType === 'task' 
        ? `/audit/action-items/${entityId}/comments/`
        : `/audit/subtasks/${entityId}/comments/`;
      
      const response = await api.post(endpoint, {
        comment_text: commentText
      });
      return response.data;
    } catch (error) {
      console.error(`Error creating ${entityType} comment:`, error);
      throw error;
    }
  },
};

// =============================================================================
// Billing API Types
// =============================================================================

export interface BillingStatus {
  type: 'consultant' | 'client';
  is_always_free?: boolean;
  subscription_status?: 'trial' | 'active' | 'grace' | 'expired';
  trial_days_remaining?: number;
  trial_audit_used?: boolean;
  audit_credits_balance?: number;
  has_payment_method?: boolean;
  billing_responsibility?: 'consultant' | 'client';
  message?: string;
  error?: string;
}

export interface CreditPurchase {
  id: number;
  pack_type: 'starter' | 'professional' | 'scale';
  credits_purchased: number;
  credits_remaining: number;
  purchased_at: string;
  expires_at: string;
}

export interface CreditBalanceResponse {
  balance: number | null;
  purchases: CreditPurchase[];
  message?: string;
}

export interface ClientBillingSettings {
  billing_responsibility: 'consultant' | 'client';
  allow_self_service: boolean;
  audit_credits_balance: number;
  trial_days_remaining: number;
  trial_audit_used: boolean;
}

export interface ConsultantSettingsResponse {
  company_display_name?: string;
  logo_url?: string;
  company_tagline?: string;
  primary_color?: string;
  secondary_color?: string;
  default_billing_responsibility: 'consultant' | 'client';
  allow_client_self_service: boolean;
}

// =============================================================================
// Billing API
// =============================================================================

export const billingAPI = {
  // Get current billing status (cached for 30 seconds with deduplication)
  getBillingStatus: async (): Promise<BillingStatus> => {
    return requestCache.deduplicate(
      '/billing/status/',
      async () => {
        const response = await api.get('/billing/status/');
        return response.data;
      },
      30 * 1000 // 30 seconds cache
    );
  },

  // Create checkout session for subscription or credit pack
  createCheckoutSession: async (
    type: 'subscription' | 'starter' | 'professional' | 'scale',
    companyId?: number
  ): Promise<{ checkout_url: string }> => {
    const response = await api.post('/billing/create-checkout-session/', {
      type,
      company_id: companyId,
      base_url: window.location.origin,
    });
    return response.data;
  },

  // Create customer portal session for subscription management
  createPortalSession: async (): Promise<{ portal_url: string }> => {
    const response = await api.post('/billing/create-portal-session/', {
      base_url: window.location.origin,
    });
    return response.data;
  },

  // Get credit balance and purchase history
  getCreditBalance: async (): Promise<CreditBalanceResponse> => {
    const response = await api.get('/billing/credits/');
    return response.data;
  },

  // Get client billing settings (consultant view) - cached with deduplication
  getClientBilling: async (companyId: number): Promise<ClientBillingSettings> => {
    return requestCache.deduplicate(
      `/billing/client/${companyId}/`,
      async () => {
        const response = await api.get(`/billing/client/${companyId}/`);
        return response.data;
      },
      30 * 1000 // 30 seconds cache
    );
  },

  // Update client billing settings (consultant only)
  updateClientBilling: async (
    companyId: number, 
    data: { billing_responsibility?: 'consultant' | 'client'; allow_self_service?: boolean | null }
  ): Promise<{ status: string }> => {
    const response = await api.put(`/billing/client/${companyId}/`, data);
    return response.data;
  },

  // Get consultant settings
  getConsultantSettings: async (): Promise<ConsultantSettingsResponse> => {
    const response = await api.get('/billing/settings/');
    return response.data;
  },

  // Update consultant settings
  updateConsultantSettings: async (data: Partial<ConsultantSettingsResponse>): Promise<{ status: string }> => {
    const response = await api.put('/billing/settings/', data);
    return response.data;
  },
};

// =============================================================================
// KPI API Types
// =============================================================================

export interface KPI {
  id: number;
  name: string;
  description?: string;
  unit_type: 'percentage' | 'currency' | 'number' | 'days' | 'count';
  target_value: string;
  tolerance_percentage: string;
  higher_is_better: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export interface KPIEntry {
  id: number;
  kpi_id: number;
  period_date: string;
  actual_value: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface KPIGridData {
  kpis: KPI[];
  periods: string[];
  entries: Record<string, KPIEntry>; // key is "kpiId_period"
  count: number;
}

// =============================================================================
// KPI API
// =============================================================================

export const kpiAPI = {
  // Get KPI grid data for company
  getKPIGrid: async (companyId: number): Promise<KPIGridData> => {
    console.log('🔍 [KPI API] Calling /kpis/${companyId}/grid/');
    console.log('🔍 [KPI API] Auth header:', api.defaults.headers.common['Authorization']);
    const response = await api.get(`/kpis/${companyId}/grid/`);
    return response.data;
  },

  // Get all KPIs for a company
  getCompanyKPIs: async (companyId: number): Promise<{ kpis: KPI[]; count: number }> => {
    const response = await api.get(`/kpis/${companyId}/`);
    return response.data;
  },

  // Create new KPI
  createKPI: async (companyId: number, kpiData: Partial<KPI>) => {
    const response = await api.post(`/kpis/${companyId}/create/`, kpiData);
    return response.data;
  },

  // Update KPI
  updateKPI: async (kpiId: number, kpiData: Partial<KPI>) => {
    const response = await api.put(`/kpis/${kpiId}/update/`, kpiData);
    return response.data;
  },

  // Delete KPI (soft delete)
  deleteKPI: async (kpiId: number) => {
    const response = await api.delete(`/kpis/${kpiId}/delete/`);
    return response.data;
  },

  // Create or update KPI entry
  createKPIEntry: async (entryData: {
    kpi_id: number;
    period_date: string;
    actual_value: string;
    notes?: string;
  }) => {
    const response = await api.post('/kpi-entries/', entryData);
    return response.data;
  },

  // Bulk create/update KPI entries
  bulkCreateKPIEntries: async (entries: Array<{
    kpi_id: number;
    period_date: string;
    actual_value: string;
    notes?: string;
  }>) => {
    const response = await api.post('/kpi-entries/bulk/', { entries });
    return response.data;
  },

  // Export KPIs to CSV
  exportKPIsCSV: async (companyId: number) => {
    const response = await api.get(`/kpis/${companyId}/export/csv/`, {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `kpis-company-${companyId}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return response.data;
  },

  // Update KPI tracking settings for company
  updateKPISettings: async (companyId: number, settings: {
    tracking_enabled?: boolean;
    tracking_frequency?: 'monthly' | 'quarterly' | 'annual';
  }) => {
    const response = await api.put(`/companies/${companyId}/kpi-settings/`, settings);
    return response.data;
  },

  // Template KPI Management
  getTemplateKPIs: async (templateId: number) => {
    const response = await api.get(`/templates/${templateId}/kpis/`);
    return response.data;
  },

  addTemplateKPI: async (templateId: number, kpiData: {
    kpi_name: string;
    kpi_description?: string;
    unit_type?: string;
  }) => {
    const response = await api.post(`/templates/${templateId}/kpis/add/`, kpiData);
    return response.data;
  },

  deleteTemplateKPI: async (templateId: number, mappingId: number) => {
    const response = await api.delete(`/templates/${templateId}/kpis/${mappingId}/delete/`);
    return response.data;
  },

  // KPI Matching
  matchTemplateKPIsToCompany: async (templateId: number, companyId: number) => {
    const response = await api.post('/kpis/match/', {
      template_id: templateId,
      company_id: companyId,
    });
    return response.data;
  },

  suggestKPIsForWizard: async (templateName: string, structure: any) => {
    const response = await api.post('/kpis/suggest-for-wizard/', {
      template_name: templateName,
      structure,
    });
    return response.data;
  },

  linkAuditToKPIs: async (auditId: number, kpiIds: number[]) => {
    const response = await api.post(`/audits/${auditId}/link-kpis/`, {
      kpi_ids: kpiIds,
    });
    return response.data;
  },

  // Generate KPIs automatically for a company
  generateKPIsForCompany: async (companyId: number) => {
    const response = await api.post(`/kpis/${companyId}/generate/`, {});
    return response.data;
  },

  // Get detailed audit information for a KPI
  getKPIAuditDetails: async (kpiId: number) => {
    const response = await api.get(`/kpis/${kpiId}/audit-details/`);
    return response.data;
  },
};

// Excel Template Import API (Standalone / Beta)
export const excelImportAPI = {
  startImport: async (file: File, sheetName?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (sheetName) {
      formData.append('sheet_name', sheetName);
    }

    const response = await api.post('/audit/ai-builder/excel-import/start/', formData, {
      headers: {
        // Let axios set the multipart boundary
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data as {
      success: boolean;
      session_id: string;
      task_id: string;
      status: string;
      sheet_name?: string;
    };
  },

  getProgress: async (sessionId: string) => {
    const response = await api.get(`/audit/ai-builder/excel-import/progress/${sessionId}/`);
    return response.data as {
      session_id: string;
      status: 'uploaded' | 'processing' | 'preview' | 'failed';
      is_complete: boolean;
      error_message?: string | null;
      meta?: any;
      template_preview?: any;
      workbook_summary?: any;
      created_template_id?: number | null;
    };
  },

  createDraft: async (sessionId: string) => {
    const response = await api.post('/audit/ai-builder/excel-import/create-draft/', { session_id: sessionId });
    return response.data as { success: boolean; template_id: number };
  },
};

export default api; 